import { Box, styled } from '@mui/material';
import { useMemo } from 'react';

import {
  EResourceResponseType,
  type AircraftResourceResponse,
  type CaseLogisticsResponse,
  type LogisticsAircraftById,
  type LogisticsLocationsById,
  type LogisticsRouteResponse,
} from '#entities';

import { buildLegChains } from '../lib';

import { CaseLegItem } from './CaseLegItem';

interface CaseLegsProps {
  route: LogisticsRouteResponse | null;
  locations?: CaseLogisticsResponse['locations'];
  resources?: CaseLogisticsResponse['resources'];
  isItalian?: boolean;
}

const Root = styled(Box)(({ theme: { spacing, palette } }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing(1),
  color: palette.common.white,
  overflow: 'auto',
  WebkitOverflowScrolling: 'touch',

  scrollbarWidth: 'none',
  msOverflowStyle: 'none',

  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

export const CaseLegs = ({
  route,
  locations,
  resources,
  isItalian = false,
}: CaseLegsProps) => {
  const chains = buildLegChains(route?.legs ?? []);

  const locationsById = useMemo<LogisticsLocationsById>(
    () => new Map((locations ?? []).map(l => [l.id, l])),
    [locations],
  );

  const aircraftById = useMemo<LogisticsAircraftById>(
    () =>
      new Map(
        (resources ?? [])
          .filter(
            (r): r is AircraftResourceResponse =>
              r.$type === EResourceResponseType.Aircraft,
          )
          .map(r => [r.resourceId, r]),
      ),
    [resources],
  );

  return (
    <Root>
      {chains.map((chain, i) => (
        <Box key={i} display="flex" flexDirection="column" gap={0.5}>
          {chain.map(leg => (
            <CaseLegItem
              key={leg.id}
              leg={leg}
              locationsById={locationsById}
              aircraftById={aircraftById}
              isItalian={isItalian}
            />
          ))}
        </Box>
      ))}
    </Root>
  );
};
